import { useMemo } from 'react';
import type {
  InboundResponse,
  InboundRequest,
  OrderListParams,
  OrderListResponse,
  OrderSummary,
  StockStatusResponse,
  TraceResponse,
  AuditHistoryEntry,
  AuditHistoryEntryInput,
  CloneOrderRequest,
  MergeOrdersRequest,
  OrderCancelInfo,
  OrderFulfillmentStatusListResponse,
  OrderUpdateRequest,
  CreateShipmentRequest,
  SwitchShipmentProviderRequest,
  SwitchShipmentProviderResponse,
  RefreshShippingStatusResponse,
  WmsOrderSearchParams,
} from '../../../infrastructure/OMSClient';
import { OMSClient } from '../../../infrastructure/OMSClient';
import { getConfig } from '../../../config';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import { getMonthsBetweenDates } from '../../../utils/dateUtils';
import type { InboundStatus } from '../../../models/InboundStatus';

export const useOMS = () => {
  const config = getConfig();
  // ORDER routes to internal-api; inbound + fulfilment stay on facade (config.apiUrl) since they're not on prod internal-api.
  const orderApiUrl = getAdminApiUrl(AdminApiDomain.ORDER);
  const omsClient = useMemo(
    () => new OMSClient(orderApiUrl, config.apiKey, config.apiUrl),
    [orderApiUrl, config.apiKey, config.apiUrl],
  );

  const listOrdersByDateRange = async (
    startDate: string,
    endDate: string,
    onPage?: (pageOrders: OrderSummary[]) => void,
  ): Promise<OrderSummary[]> => {
    try {
      // Get all months between start and end dates
      const months = getMonthsBetweenDates(startDate, endDate);

      if (months.length === 0) {
        console.error('No valid months in date range', { startDate, endDate });
        return [];
      }

      // Fetch orders for each month in parallel; stream pages to caller via onPage.
      const orderPromises = months.map((month) => omsClient.listOrders({ orderMonth: month, limit: 1000 }, onPage));
      const results = await Promise.all(orderPromises);

      // Merge and deduplicate orders by ID
      const allOrders: OrderSummary[] = [];
      const orderIds = new Set<string>();

      results.forEach((result) => {
        if (result && Array.isArray(result.orders)) {
          result.orders.forEach((order) => {
            if (order.orderId && !orderIds.has(order.orderId)) {
              orderIds.add(order.orderId);
              allOrders.push(order);
            }
          });
        }
      });

      return allOrders;
    } catch (error) {
      console.error('Error fetching orders for date range:', error);
      return [];
    }
  };

  const listOrders = async (params: OrderListParams): Promise<OrderListResponse> => {
    console.log('listOrders request', params);
    const response = await omsClient.listOrders(params);
    console.log('listOrders response', response);
    return response;
  };

  // Single-page fetch for the paginated list view. Returns one page plus the opaque
  // lastEvaluatedKey cursor; the caller owns cursor history.
  const listOrdersPage = async (params: OrderListParams): Promise<OrderListResponse> => {
    console.debug('listOrdersPage request', params);
    const response = await omsClient.listOrdersPage(params);
    console.debug('listOrdersPage response', {
      count: response.orders.length,
      hasNext: Boolean(response.lastEvaluatedKey),
    });
    return response;
  };

  const getOrderDetail = async (orderId: string): Promise<OrderSummary | null> => {
    console.log('getOrderDetail request', orderId);
    const response = await omsClient.getOrderDetail(orderId);
    console.log('getOrderDetail response', response);
    return response;
  };

  const updateOrder = async (orderId: string, request: OrderUpdateRequest): Promise<OrderSummary | null> => {
    console.log('updateOrder request', orderId, request);
    const response = await omsClient.updateOrder(orderId, request);
    console.log('updateOrder response', response);
    return response;
  };

  const updateOrderStatus = async (orderId: string, status: string, operator: string): Promise<OrderSummary | null> => {
    console.log('updateOrderStatus request', orderId, status, operator);
    const response = await omsClient.updateOrderStatus(orderId, status, operator);
    console.log('updateOrderStatus response', response);
    return response;
  };

  const requestInbound = async (orderId: string, inboundRequest: InboundRequest): Promise<InboundResponse | null> => {
    console.log('requestInbound request', orderId, inboundRequest);
    const response = await omsClient.requestInbound(orderId, inboundRequest);
    console.log('requestInbound response', response);
    return response;
  };

  const getStockStatus = async (skus: string): Promise<StockStatusResponse | null> => {
    console.log('getStockStatus request', skus);
    const response = await omsClient.getStockStatus(skus);
    console.log('getStockStatus response', response);
    return response;
  };

  const createDeleoOrder = async (orderId: string, reason: string): Promise<OrderSummary | null> => {
    return omsClient.createDeleoOrder(orderId, reason);
  };

  const createAutoRoutedShipment = async (orderId: string, reason: string): Promise<OrderSummary | null> => {
    return omsClient.createAutoRoutedShipment(orderId, reason);
  };

  const createShipment = async (orderId: string, request: CreateShipmentRequest): Promise<OrderSummary | null> => {
    return omsClient.createShipment(orderId, request);
  };

  const resetDeleoOrder = async (orderId: string, reason: string): Promise<OrderSummary | null> => {
    return omsClient.resetDeleoOrder(orderId, reason);
  };

  const resetParxlOrder = async (orderId: string, reason: string): Promise<OrderSummary | null> => {
    return omsClient.resetParxlOrder(orderId, reason);
  };

  const resetYunOrder = async (orderId: string, reason: string): Promise<OrderSummary | null> => {
    return omsClient.resetYunOrder(orderId, reason);
  };

  const refreshShippingStatus = async (orderId: string): Promise<RefreshShippingStatusResponse> => {
    console.debug('refreshShippingStatus request', orderId);
    const response = await omsClient.refreshShippingStatus(orderId);
    console.debug('refreshShippingStatus response', response);
    return response;
  };

  const getDeliveryTrace = async (orderId: string): Promise<TraceResponse> => {
    console.log('getDeliveryTrace request', orderId);
    const response = await omsClient.getDeliveryTrace(orderId);
    console.log('getDeliveryTrace response', response);
    return response;
  };

  const cancelInbound = async (
    orderId: string,
    inboundId: string,
    auditEntry: AuditHistoryEntryInput,
  ): Promise<InboundResponse | null> => {
    console.log('cancelInbound request', orderId, inboundId, auditEntry);
    const response = await omsClient.cancelInbound(orderId, inboundId, auditEntry);
    console.log('cancelInbound response', response);
    return response;
  };

  const cancelOrder = async (
    orderId: string,
    cancelInfo: OrderCancelInfo,
    auditEntry: AuditHistoryEntryInput,
  ): Promise<OrderSummary | null> => {
    console.log('cancelOrder request', orderId, cancelInfo, auditEntry);
    const response = await omsClient.cancelOrder(orderId, cancelInfo, auditEntry);
    console.log('cancelOrder response', response);
    return response;
  };

  const listAuditHistory = async (orderId: string): Promise<AuditHistoryEntry[]> => {
    console.log('listAuditHistory request', orderId);
    const response = await omsClient.listAuditHistory(orderId);
    console.log('listAuditHistory response', response);
    return response;
  };

  const cloneOrder = async (cloneOrderRequest: CloneOrderRequest): Promise<OrderSummary | null> => {
    return omsClient.cloneOrder(cloneOrderRequest);
  };

  const switchShipmentProvider = async (
    orderId: string,
    request: SwitchShipmentProviderRequest,
  ): Promise<SwitchShipmentProviderResponse> => {
    return omsClient.switchShipmentProvider(orderId, request);
  };

  const mergeOrders = async (mergeOrdersRequest: MergeOrdersRequest): Promise<OrderSummary | null> => {
    console.log('mergeOrders request', mergeOrdersRequest);
    const response = await omsClient.mergeOrders(mergeOrdersRequest);
    console.log('mergeOrders response', response);
    return response;
  };

  const getOrderByTracking = async (trackingNo: string): Promise<OrderSummary[]> => {
    // Extract tracking number before '|' separator if exists
    const cleanTrackingNo = trackingNo.split('|')[0] || trackingNo;
    console.log('getOrderByTracking request', { original: trackingNo, cleaned: cleanTrackingNo });
    const response = await omsClient.getOrderByTracking(cleanTrackingNo);
    console.log('getOrderByTracking response', response);
    return response;
  };

  const searchWmsOrders = async (params: WmsOrderSearchParams): Promise<OrderSummary[]> => {
    console.debug('searchWmsOrders request', {
      searchBy: params.trackingNumber ? 'trackingNumber' : 'inboundTrackingOrderNumber',
    });
    const response = await omsClient.searchWmsOrders(params);
    console.debug('searchWmsOrders response', { count: response.length });
    return response;
  };

  const updateLineItemInboundStatus = async (
    orderId: string,
    itemId: string,
    inboundStatus: InboundStatus,
    operator: string,
  ): Promise<OrderSummary | null> => {
    try {
      // First get the current order details
      const currentOrder = await getOrderDetail(orderId);
      if (!currentOrder) {
        throw new Error('Order not found');
      }

      // Find and update the specific line item
      const updatedLineItems = currentOrder.lineItems.map((item) =>
        item.itemId === itemId ? { ...item, inboundStatus } : item,
      );

      // Update the order with the modified line items
      const auditEntry: AuditHistoryEntryInput = {
        operator,
        source: 'ADMIN_WMS',
      };

      const request: OrderUpdateRequest = {
        lineItems: updatedLineItems,
        auditEntry,
      };

      const response = await updateOrder(orderId, request);

      console.log('updateLineItemInboundStatus response', response);
      return response;
    } catch (error) {
      console.error('Error updating line item inbound status:', error);
      throw error;
    }
  };

  const getFulfillmentStatus = async (): Promise<OrderFulfillmentStatusListResponse> => {
    const response = await omsClient.getFulfillmentStatus();
    return response;
  };

  const createOrder = async (orderId: string): Promise<OrderSummary | null> => {
    console.log('createOrder request', orderId);
    const response = await omsClient.createOrder(orderId);
    console.log('createOrder response', response);
    return response;
  };

  return {
    listOrders,
    listOrdersPage,
    listOrdersByDateRange,
    getOrderDetail,
    updateOrder,
    updateOrderStatus,
    requestInbound,
    getStockStatus,
    createDeleoOrder,
    createAutoRoutedShipment,
    createShipment,
    resetDeleoOrder,
    resetParxlOrder,
    resetYunOrder,
    refreshShippingStatus,
    getDeliveryTrace,
    cancelInbound,
    cancelOrder,
    listAuditHistory,
    cloneOrder,
    switchShipmentProvider,
    mergeOrders,
    getOrderByTracking,
    searchWmsOrders,
    updateLineItemInboundStatus,
    getFulfillmentStatus,
    createOrder,
  };
};
